import moment from 'moment';

// Import real calendar services
const {
  getCalendarSettings: getRealCalendarSettings,
  updateCalendarSettings: updateRealCalendarSettings,
  generateAuthURl: generateRealAuthURL,
  generateToken: generateRealToken,
  disconnectCalendar: disconnectRealCalendar,
  getCalendarEventsV2,
  addEvent,
  updateEvent: updateEventAPI,
  deleteEvent: deleteEventAPI,
  getSideBarUsers,
} = require('services/Calender/calendarServices');

/**
 * Fetch grouped events for month/week/year views
 * Returns 2 events per day + more_events_count
 *
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} viewType - View type (month, week, year)
 * @param {object} filters - Applied filters
 * @param {array} userIds - Selected user IDs
 * @param {string} timezone - User timezone
 * @returns {Promise<{data: object}>} Grouped events by date
 */
export const fetchCalendarEvents = async (startDate, endDate, viewType, userIds, timezone, case_id, contact_id, filters) => {
  // Convert dates to UTC ISO format
  const startUTC = moment.tz(startDate, timezone).startOf('day').utc().format();
  const endUTC = moment.tz(endDate, timezone).subtract(1, 'day').endOf('day').utc().format();
  let view = 'month';
  let year = null;
  if (viewType === 'multiMonthYear') {
    view = 'year';
    year = moment.tz(startDate, timezone).format('YYYY');
  } else if (viewType === 'dayGridMonth') {
    view = 'month';
  } else if (viewType === 'timeGridDay') {
    view = 'day';
  } else if (viewType === 'listWeek') {
    view = 'agenda';
  } else if (viewType === 'timeGridWeek') {
    view = 'week';
  }

  return getCalendarEventsV2({
    view,
    timezone,
    startUTC,
    endUTC,
    userIds,
    limitPerDay: 2,
    year: year,
    case_id,
    contact_id,
    filters,
  });
};

/**
 * Create a new event
 *
 * @param {object} eventData - Event data
 * @returns {Promise<{data: object}>} Created event
 */
export const createEvent = async (eventData) => {
  // Extract case_id and is_lead from eventData if present
  const { case_id, is_lead } = eventData;

  const result = await addEvent({
    data: eventData,
    case_id: case_id || '',
    is_lead: is_lead || false,
    timeEntryData: '',
  });

  return result;
};

/**
 * Update an existing event
 *
 * @param {string} eventId - Event ID
 * @param {object} eventData - Updated event data
 * @returns {Promise<{data: object}>} Updated event
 */
export const updateEvent = async (eventId, eventData) => {
  const {
    case_id,
    is_lead,
    reminder_updated = false,
    send_update_notification = true,
    recurrence_scope = '',
    overriden_start_date,
    is_date_changed = false,
    ...eventPayload
  } = eventData;

  // Use real API call
  const result = await updateEventAPI({
    data: { ...eventPayload, event_id: eventId, case_id: case_id },
    case_id: case_id || '',
    is_lead: is_lead || false,
    timeEntryData: '',
    reminder_updated,
    send_update_notification,
    recurrence_scope,
    overriden_start_date,
    is_date_changed,
  });

  return result;
};

/**
 * Delete an event
 *
 * @param {string} eventId - Event ID (pk format)
 * @param {object} options - Delete options
 * @param {boolean} options.is_lead - Whether event is associated with a lead
 * @param {boolean} options.is_recurring - Whether event is recurring
 * @param {string} options.recurrence_scope - Scope: 'current', 'futures', or empty for all
 * @param {string} options.overriden_start_date - Start date of the occurrence being deleted (ISO 8601)
 * @returns {Promise<{data: {success: boolean, event_id: string}}>}
 */
export const deleteEvent = async (eventId, options = {}) => {
  const { is_lead = false, is_recurring = false, recurrence_scope = '', overriden_start_date = '' } = options;

  // Use real API call - adapt eventId to pk format expected by the API
  const result = await deleteEventAPI({
    pk: eventId,
    is_lead,
    timeEntryData: '',
    data: {
      is_recurring,
    },
    recurrence_scope,
    overriden_start_date,
  });

  return result;
};

/**
 * Fetch calendar settings
 *
 * @returns {Promise<{data: object}>} Calendar settings
 */
export const fetchCalendarSettings = async () => {
  try {
    const response = await getRealCalendarSettings();
    return response;
  } catch (error) {
    console.error('Failed to fetch calendar settings:', error);
    // Fallback to mock data on error
    console.error('Failed to fetch calendar settings:', error);
  }
};

/**
 * Update calendar settings
 *
 * @param {object} settings - Updated settings
 * @returns {Promise<{data: object}>} Updated settings
 */
export const updateCalendarSettings = async (settings) => {
  // Use real API
  try {
    const response = await updateRealCalendarSettings(settings);
    return response;
  } catch (error) {
    console.error('Failed to update calendar settings:', error);
    throw error;
  }
};

/**
 * Fetch users for sidebar (REAL API - not mocked)
 * Uses getRdsUserList from team management service
 *
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @param {string} search - Search query (keyWord)
 * @returns {Promise<{data: {firm_users: Array}}>} Users list with firm_users array
 *
 * Response format:
 * {
 *   data: {
 *     firm_users: [
 *       {
 *         firm_user_id: string,
 *         first_name: string,
 *         last_name: string,
 *         email: string,
 *         full_name: string,
 *         status: string,
 *         ...
 *       }
 *     ]
 *   }
 * }
 */
export const fetchUsers = async (page, limit, selectedUsers = [], search = '') => {
  // Use the real getSideBarUsers API from calendar services

  return getSideBarUsers({
    page,
    limit,
    search_keyword: search,
    selected_firm_user_ids: page === 1 ? selectedUsers : [],
  });
};

/**
 * Generate Google Calendar authentication URL
 *
 * @param {string} firmUserId - Firm user ID
 * @returns {Promise<{data: {auth_url: string}}>} Auth URL response
 */
export const generateGoogleAuthURL = async (firmUserId) => {
  try {
    return await generateRealAuthURL(firmUserId);
  } catch (error) {
    console.error('Failed to generate Google auth URL:', error);
    throw error;
  }
};

/**
 * Generate Google Calendar token from auth code
 *
 * @param {string} firmUserId - Firm user ID
 * @param {string} code - Authorization code
 * @param {string} state - State parameter
 * @returns {Promise<{data: object}>} Token response
 */
export const generateGoogleToken = async (firmUserId, code, state) => {
  try {
    return await generateRealToken(firmUserId, code, state);
  } catch (error) {
    console.error('Failed to generate Google token:', error);
    throw error;
  }
};

/**
 * Disconnect Google Calendar
 *
 * @param {string} firmUserId - Firm user ID
 * @returns {Promise<{data: object}>} Disconnect response
 */
export const disconnectGoogleCalendar = async (firmUserId) => {
  try {
    return await disconnectRealCalendar(firmUserId);
  } catch (error) {
    console.error('Failed to disconnect Google calendar:', error);
    throw error;
  }
};
